// Postausgang (Dokumente › Postausgang): alle E-POST-Briefe – Laden, Filtern, Serien bündeln, Monatsübersicht.
// Reine Funktionen unten sind getestet (postausgang.test.ts).
import { supabase } from '../supabase/client';
import type { Empfaenger } from '../../supabase/functions/epost-brief/logik';
import type { BriefEntwurf } from './epost';
import { postausgangDateiPfad } from '../dokumente/postausgangOrdner';

export interface AusgangBrief {
  id: string; letter_id: number | null; test: boolean; status_id: number | null; status_details: string | null;
  fehler: { code?: string; description?: string }[] | null; seiten: number | null;
  einschreiben: string | null; einschreiben_nr: string | null; betreff: string | null; empfaenger: Empfaenger;
  empfaenger_typ: string | null; empfaenger_id: string | null; bezug_typ: string | null; bezug_id: string | null; batch_id: number | null;
  ablage_datei_id: string | null;
  entwurf: BriefEntwurf | null; ersetzt_durch: string | null;
  optionen: { sammelversand?: boolean } | null; created_at: string; gedruckt_at: string | null;
  objekt: { id: string; objektnummer: string | null; bezeichnung: string | null } | null;
}

export type StandFilter = 'alle' | 'unterwegs' | 'versendet' | 'fehler' | 'wartet';

export async function ladePostausgang(seitTagen: number | null): Promise<AusgangBrief[]> {
  let q = supabase.from('epost_briefe')
    .select('id, letter_id, test, status_id, status_details, fehler, seiten, einschreiben, einschreiben_nr, betreff, empfaenger, empfaenger_typ, empfaenger_id, bezug_typ, bezug_id, batch_id, ablage_datei_id, entwurf, ersetzt_durch, optionen, created_at, gedruckt_at, objekt:objekt_id(id, objektnummer, bezeichnung)')
    .order('created_at', { ascending: false }).limit(2000);
  if (seitTagen) q = q.gte('created_at', new Date(Date.now() - seitTagen * 864e5).toISOString());
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as unknown as AusgangBrief[];
}

export const sammelFreigeben = async (letterIds: number[]) => rufeEdge({ aktion: 'sammel-freigeben', letter_ids: letterIds });
export const sammelAbbrechen = async (letterIds: number[]) => rufeEdge({ aktion: 'sammel-abbrechen', letter_ids: letterIds });

async function rufeEdge(body: Record<string, unknown>): Promise<void> {
  const { error } = await supabase.functions.invoke('epost-brief', { body });
  if (error) {
    let text = error.message;
    const ctx = (error as { context?: Response }).context;
    if (ctx && typeof ctx.json === 'function') { try { const j = await ctx.json(); if (j?.error) text = String(j.error); } catch { /* leer */ } }
    throw new Error(text);
  }
}

// ── reine Logik ─────────────────────────────────────────────────────────────

/** Wartet bei der Post auf den Sammelversand (Mindestmenge / Freitag). */
export const wartetSammel = (b: Pick<AusgangBrief, 'optionen' | 'status_id' | 'test'>) =>
  !!b.optionen?.sammelversand && (b.status_id === null || b.status_id === 1);

export function stand(b: Pick<AusgangBrief, 'optionen' | 'status_id' | 'test'> & { ersetzt_durch?: string | null }): Exclude<StandFilter, 'alle'> | 'abgebrochen' | 'ersetzt' {
  if (b.ersetzt_durch) return 'ersetzt';
  if (b.status_id === 4) return 'versendet';
  if (b.status_id === 99) return 'fehler';
  if (b.status_id === -1) return 'abgebrochen';
  if (wartetSammel(b)) return 'wartet';
  return 'unterwegs';
}

export function filtere(briefe: AusgangBrief[], f: { stand: StandFilter; mitTest: boolean; suche: string }): AusgangBrief[] {
  const s = f.suche.trim().toLowerCase();
  return briefe.filter((b) => (f.mitTest || !b.test)
    && (f.stand === 'alle' || stand(b) === f.stand)
    && (!s || [b.betreff, ...(b.empfaenger?.zeilen ?? []), b.empfaenger?.ort, b.objekt?.objektnummer, b.objekt?.bezeichnung, String(b.letter_id ?? '')]
      .some((x) => (x ?? '').toLowerCase().includes(s))));
}

/**
 * Korrekturketten: ein abgelehnter Brief, der korrigiert neu gesendet wurde (ersetzt_durch), erscheint nicht mehr
 * als eigene Zeile, sondern unter der neuesten Fassung („frühere Fassungen“, neueste zuerst). Fehlt die neue
 * Fassung in der Liste (z. B. Zeitraum), bleibt der alte Brief sichtbar.
 */
export function mitVorgaengern<T extends { id: string; ersetzt_durch?: string | null }>(briefe: T[]): { brief: T; vorgaenger: T[] }[] {
  const nachId = new Map(briefe.map((b) => [b.id, b]));
  const ende = (b: T): T => {
    let x = b; const gesehen = new Set<string>();
    while (x.ersetzt_durch && nachId.has(x.ersetzt_durch) && !gesehen.has(x.id)) { gesehen.add(x.id); x = nachId.get(x.ersetzt_durch)!; }
    return x;
  };
  const vorg = new Map<string, T[]>();
  for (const b of briefe) {
    const e = ende(b);
    if (e.id !== b.id) vorg.set(e.id, [...(vorg.get(e.id) ?? []), b]);
  }
  return briefe.filter((b) => ende(b).id === b.id).map((b) => ({ brief: b, vorgaenger: vorg.get(b.id) ?? [] }));
}

export type Zeile = { art: 'brief'; brief: AusgangBrief; vorgaenger: AusgangBrief[] } | { art: 'serie'; batchId: number; briefe: AusgangBrief[]; betreff: string; created_at: string };

/** Briefe eines Serienbrief-Laufs (gleiche batch_id) zu einer Zeile bündeln; Reihenfolge bleibt (neueste zuerst). */
export function buendle(alle: AusgangBrief[]): Zeile[] {
  const zeilen: Zeile[] = [];
  const serien = new Map<number, Extract<Zeile, { art: 'serie' }>>();
  for (const { brief: b, vorgaenger } of mitVorgaengern(alle)) {
    if (!b.batch_id) { zeilen.push({ art: 'brief', brief: b, vorgaenger }); continue; }
    let s = serien.get(b.batch_id);
    if (!s) { s = { art: 'serie', batchId: b.batch_id, briefe: [], betreff: b.betreff ?? 'Serienbrief', created_at: b.created_at }; serien.set(b.batch_id, s); zeilen.push(s); }
    s.briefe.push(b);
  }
  return zeilen.map((z) => (z.art === 'serie' && z.briefe.length === 1 ? { art: 'brief', brief: z.briefe[0], vorgaenger: [] } : z));
}

export function zaehleStaende(briefe: (Pick<AusgangBrief, 'optionen' | 'status_id' | 'test'> & { ersetzt_durch?: string | null })[]): Record<ReturnType<typeof stand>, number> {
  const n = { unterwegs: 0, versendet: 0, fehler: 0, wartet: 0, abgebrochen: 0, ersetzt: 0 };
  for (const b of briefe) n[stand(b)]++;
  return n;
}

export interface Monat { monat: string; anzahl: number; seiten: number; einschreiben: number; nachObjekt: { objekt: string; anzahl: number }[] }

/** Je Monat (Erstellung): Briefe, Seiten, Einschreiben, Verteilung je Objekt – ohne Testbriefe und Abgebrochene. */
export function monatsUebersicht(briefe: AusgangBrief[]): Monat[] {
  const monate = new Map<string, { anzahl: number; seiten: number; einschreiben: number; objekte: Map<string, number> }>();
  for (const b of briefe) {
    if (b.test || b.status_id === -1 || b.status_id === 99) continue;
    const monat = b.created_at.slice(0, 7);
    const m = monate.get(monat) ?? { anzahl: 0, seiten: 0, einschreiben: 0, objekte: new Map() };
    m.anzahl++; m.seiten += b.seiten ?? 0; if (b.einschreiben) m.einschreiben++;
    const o = b.objekt?.objektnummer ? `${b.objekt.objektnummer}${b.objekt.bezeichnung ? ` ${b.objekt.bezeichnung}` : ''}` : 'Ohne Objekt';
    m.objekte.set(o, (m.objekte.get(o) ?? 0) + 1);
    monate.set(monat, m);
  }
  return [...monate.entries()].sort(([a], [b]) => b.localeCompare(a)).map(([monat, m]) => ({
    monat, anzahl: m.anzahl, seiten: m.seiten, einschreiben: m.einschreiben,
    nachObjekt: [...m.objekte.entries()].map(([objekt, anzahl]) => ({ objekt, anzahl })).sort((a, b) => b.anzahl - a.anzahl),
  }));
}

/** „2026-10“ → „Oktober 2026“ */
export const monatName = (m: string) => new Date(`${m}-01T12:00:00`).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });

/** Detailseite des Empfängers (sofern bekannt). */
export function empfaengerPfad(b: Pick<AusgangBrief, 'empfaenger_typ' | 'empfaenger_id'>): string | null {
  if (!b.empfaenger_id) return null;
  const id = encodeURIComponent(b.empfaenger_id);
  if (b.empfaenger_typ === 'mieter') return `/admin/mieter/${id}?panel=briefe`;
  if (b.empfaenger_typ === 'eigentuemer') return `/admin/eigentuemer/${id}?panel=briefe`;
  if (b.empfaenger_typ === 'kontakt') return `/admin/kontakte/kontakt/${id}?tab=briefe`;
  return null;
}

/** Objekt-Detailseite. */
export const objektPfad = (b: Pick<AusgangBrief, 'objekt'>) => (b.objekt?.id ? `/admin/objekte/${encodeURIComponent(b.objekt.id)}` : null);

/** Woher der Brief kam: Dokument aus der Akte → dessen Vorschau; Serienbrief/E-Mail ohne eigene Seite. */
export function quelle(b: Pick<AusgangBrief, 'bezug_typ' | 'bezug_id'>): { label: string; pfad: string | null } | null {
  if (b.bezug_typ === 'dokument') return { label: 'Dokument', pfad: b.bezug_id ? `/admin/dokumente/${encodeURIComponent(b.bezug_id)}` : null };
  if (b.bezug_typ === 'serienbrief') return { label: 'Serienbrief', pfad: null };
  return null;
}

/** Abgelegtes Brief-PDF in der Akte (Drive-ID → Aktenschrank mit geöffneter Datei). */
export const ablagePfad = (b: Pick<AusgangBrief, 'ablage_datei_id'> & { objekt?: unknown }) =>
  (!b.ablage_datei_id ? null : b.objekt === null ? postausgangDateiPfad(b.ablage_datei_id) : `/admin/dokumente/${encodeURIComponent(b.ablage_datei_id)}`);

export const EMPF_ART: Record<string, string> = { mieter: 'Mieter', eigentuemer: 'Eigentümer', kontakt: 'Kontakt', dienstleister: 'Dienstleister' };
